import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import useCycleApi from 'common/hooks/useCycleApi'
import { ErrorEnumMessage } from 'core/api/constants/error_messages'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { COST_TRANSACTION } from '.'
import { useInvalidateCostQueries } from './invalidate'
import {
  CostTransactionBody,
  CostTransactionCategoryCreateBody,
  EditCostTransactionCategoryVariables,
} from './types'

export const useMutateCostTransactionCreate = () => {
  const cycleApi = useCycleApi()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['cost'])
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    ({ body: data }: { body: CostTransactionBody }) =>
      cycleApi.request({ ...COST_TRANSACTION.create, data }),
    {
      onSuccess: () => {
        invalidateCostQueries()
        enqueueSnackbar(t('create.transaction.success') || 'Created', { variant: 'success' })
      },
      onError: (err) => {
        enqueueSnackbar(t('create.transaction.failed') || 'Create failed', { variant: 'error' })
        console.error(err)
      },
    }
  )
}

export const useMutateCostTransactionUpdate = () => {
  const cycleApi = useCycleApi()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['cost'])
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    ({ transactionId, body: data }: { transactionId: string; body: CostTransactionBody }) =>
      cycleApi.request({
        ...COST_TRANSACTION.update,
        url: `${COST_TRANSACTION.update.url}/${transactionId}`,
        data,
      }),
    {
      onSuccess: () => {
        invalidateCostQueries()
        enqueueSnackbar(t('update.transaction.success') || 'Updated', { variant: 'success' })
      },
      onError: (err) => {
        enqueueSnackbar(t('update.transaction.failed') || 'Update failed', { variant: 'error' })
      },
    }
  )
}

export const useMutateCostTransactionCreateCategory = () => {
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    ({ body: data }: { body: CostTransactionCategoryCreateBody }) =>
      cycleApi.request({ ...COST_TRANSACTION.createCategory, data }),
    {
      onSuccess: () => {
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateCostTransactionDelete = () => {
  const cycleApi = useCycleApi()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['cost'])
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    (transactionId: string) =>
      cycleApi.request({
        ...COST_TRANSACTION.delete,
        url: `${COST_TRANSACTION.delete.url}/${transactionId}`,
      }),
    {
      onSuccess: () => {
        invalidateCostQueries()
        enqueueSnackbar(t('delete.transaction.success') || 'Deleted', { variant: 'success' })
      },
      onError: (err) => {
        enqueueSnackbar(t('delete.transaction.failed') || 'Delete failed', { variant: 'error' })
      },
    }
  )
}

export const useMutateCostTransactionEditCategory = () => {
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    ({ categoryId, body }: EditCostTransactionCategoryVariables) =>
      cycleApi.request({
        ...COST_TRANSACTION.editCategory,
        url: `${COST_TRANSACTION.editCategory.url}/${categoryId}`,
        data: body,
      }),
    {
      onSuccess: () => {
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateUpdateStockPricePerUnit = () => {
  const cycleApi = useCycleApi()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['cost'])
  const invalidateCostQueries = useInvalidateCostQueries()

  return useMutation(
    ({ stockId, price_per_unit }: { stockId: string; price_per_unit: number }) =>
      cycleApi.request({
        ...COST_TRANSACTION.updateStockPricePerUnit,
        url: `${COST_TRANSACTION.updateStockPricePerUnit.url}/${stockId}/price-per-unit`,
        data: { price_per_unit },
      }),
    {
      onSuccess: () => {
        invalidateCostQueries()
        enqueueSnackbar(t('update.price.per.unit.success'), { variant: 'success' })
      },
      onError: (error: unknown) => {
        const { response } = error as AxiosError<{ message?: string }>
        const rawMessage = response?.data?.message || ''
        const code = rawMessage.split(':').pop()?.trim() || ''
        const mapped = ErrorEnumMessage[code as keyof typeof ErrorEnumMessage]
        enqueueSnackbar(mapped || t('update.price.per.unit.failed'), { variant: 'error' })
      },
    }
  )
}
